<template>
    <a :href="item?.url ?? '#'" class="task-list-item block overflow-hidden rounded-2xl border-2 border-[#F3F3F3] text-body !no-underline">
        <div
            :class="showWorkOrderColumn ? 'xs:grid-cols-[36px_1.25fr_1fr_2fr_1.5fr_1.25fr_1fr]' : 'xs:grid-cols-[36px_1.25fr_1fr_2fr_1.5fr_1.25fr]'"
            class="grid min-h-21 grid-cols-1 gap-4 px-5 py-4 transition-colors hover:bg-gray-50 xs:items-center"
        >
            <!-- Selection -->
            <div v-if="selectable" class="hidden xs:block">
                <div class="position-relative flex items-center justify-start">
                    <task-resolution-icon
                        :bg-color="getTaskResolutionColor(item?.resolution?.value) || groupColor"
                        :overdue="!!item?.is_overdue || item?.resolution?.value === 'overdue'"
                        :success="determineTaskResolution(item)"
                    />
                    <div
                        v-if="item?.approval_required && item?.status.value === 'done'"
                        v-c-tooltip="{
                            content: getApprovalTooltip(item),
                            allowHtml: true,
                        }"
                        :class="{ 'bg-primary': item?.approved_by, 'bg-gray': !item?.approved_by }"
                        class="position-absolute -end-1 -bottom-1 z-10 rounded-full p-[3px] text-white"
                    >
                        <check v-if="item?.approved_by" height="10" width="10" />
                        <span v-else class="flex items-center justify-center" style="height: 10px; width: 10px; line-height: 10px; font-size: 12px"
                            >!</span
                        >
                    </div>
                    <spinner v-if="loading" class="absolute" size="xs" style="height: 38px; width: 38px; left: -1px" />
                </div>
            </div>
            <!-- Task -->
            <div class="flex min-w-0 items-center">
                <div v-if="showSkeleton">
                    <skeleton-loader class="m-0 w-full" />
                </div>
                <task-group-badge v-else :bg-color="getTaskResolutionColor(item?.resolution?.value) || groupColor" :group="item?.group" />
                <div v-if="isMobile" class="ml-auto underline">{{ item?.code }}</div>
            </div>

            <!-- Date Desktop -->
            <div v-if="!isMobile" class="min-w-0">
                <div class="mb-1 font-bold xs:hidden">{{ trans('tasks.overview.item.due_at.title') }}</div>
                <div v-if="showSkeleton">
                    <skeleton-loader class="m-0" />
                    <skeleton-loader class="m-0" />
                </div>
                <div v-else-if="taskResolvedOrTimedOut">
                    <div :class="getTaskResolutionColor(item?.resolution?.value, 'text')" class="font-medium">
                        {{ item.resolution?.label || trans('general.status.overdue') }}
                    </div>
                    <div v-c-tooltip="{ content: getTimeZoneInfo(item) }" class="inline-block">{{ getTaskStatusUpdatedAt(item, currentLocale) }}</div>
                </div>
                <div v-else>
                    <div v-c-tooltip="{ content: getTimeZoneInfo(item) }" class="inline-block">
                        {{ getTaskStatusUpdatedAt(item, currentLocale) }}
                    </div>
                    <div>{{ getDate(item, currentLocale) }}</div>
                </div>
            </div>

            <!-- Title Desktop -->
            <div v-if="!isMobile" class="min-w-0">
                <div class="mb-1 font-bold xs:hidden">{{ trans('tasks.overview.item.title.title') }}</div>
                <div v-if="showSkeleton">
                    <skeleton-loader class="m-0" />
                    <skeleton-loader class="m-0" />
                </div>
                <div v-else>
                    <div class="truncate">{{ item.title }}</div>
                    <div v-if="item.code" class="truncate text-body underline">{{ item.code }}</div>
                </div>
            </div>

            <!-- Location -->
            <div v-if="!isMobile" class="min-w-0">
                <div v-if="showSkeleton">
                    <skeleton-loader class="m-0" />
                    <skeleton-loader class="m-0" />
                </div>
                <div v-else>
                    <div v-if="location" class="truncate">{{ location }}</div>
                    <div class="truncate">{{ specificLocation }} ({{ airport }})</div>
                </div>
            </div>

            <!-- Asset -->
            <div v-if="!isMobile" class="min-w-0">
                <div v-if="showSkeleton">
                    <skeleton-loader class="m-0" />
                    <skeleton-loader class="m-0" />
                </div>
                <div v-else>
                    <div class="truncate">{{ item.asset.type }}/{{ item.asset.designation }}</div>
                    <div class="truncate">{{ item.asset.manufacturer.name }}</div>
                    <a
                        v-if="hasAnyPermission('asset.read')"
                        :href="route('assets.show', item.asset.id)"
                        class="truncate underline"
                        @click.prevent.stop="showAssetDetails(item.asset.id)"
                    >
                        <div class="truncate">Curie ID: {{ item.asset.curieId }}</div>
                    </a>
                    <div v-else class="truncate">Curie ID: {{ item.asset.curieId }}</div>
                </div>
            </div>

            <!-- Work Order (status grouping, approved-tasks only) -->
            <div v-if="showWorkOrderColumn && !isMobile" class="min-w-0">
                <div v-if="showSkeleton">
                    <skeleton-loader class="m-0" />
                </div>
                <template v-else-if="item.relatedWorkOrder">
                    <button
                        :disabled="!item.relatedWorkOrder.canView"
                        class="inline-flex max-w-full items-center gap-1.5 !rounded-lg border !border-red-200 bg-red-50 px-3 py-1.5 text-xs leading-none font-semibold text-danger transition-colors hover:bg-red-100"
                        data-testid="related-workorder-button"
                        type="button"
                        @click.prevent.stop="openWorkOrder(item.relatedWorkOrder.url)"
                    >
                        <FileText class="shrink-0" size="16" />
                        <span class="truncate">{{ item.relatedWorkOrder.code }}</span>
                    </button>
                </template>
            </div>

            <!-- Title & Date Mobile -->
            <div v-if="isMobile" class="-mx-5 border-b-2 px-5 pb-4">
                <div v-if="showSkeleton">
                    <skeleton-loader class="m-0" />
                    <skeleton-loader class="m-0" />
                </div>
                <div v-else>
                    <div class="truncate font-medium">{{ item.title }}</div>
                    <div class="mb-1 flex gap-1">
                        {{ trans('tasks.overview.item.due_at.title') }}:
                        <div v-if="taskResolvedOrTimedOut">
                            <div
                                v-c-tooltip="{ content: getTimeZoneInfo(item) }"
                                :class="getTaskResolutionColor(item?.resolution?.value, 'text')"
                                class="font-medium"
                            >
                                {{ getTaskStatusUpdatedAt(item) }} {{ item.resolution?.label || trans('general.status.overdue') }}
                            </div>
                        </div>
                        <div v-else>
                            <div v-c-tooltip="{ content: getTimeZoneInfo(item) }">
                                {{ getDate(item, currentLocale) }} {{ getTaskStatusUpdatedAt(item) }}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Asset & Locations Tablet -->
            <div v-if="isMobile" class="">
                <div v-if="showSkeleton">
                    <skeleton-loader class="m-0" />
                    <skeleton-loader class="m-0" />
                    <skeleton-loader class="m-0" />
                </div>
                <div v-else>
                    <div class="truncate font-medium">{{ item.asset.type }}/{{ item.asset.designation }}/{{ item.asset.manufacturer.name }}</div>
                    <div class="truncate font-medium">{{ location }} - {{ specificLocation }}</div>
                    <div class="truncate font-medium">
                        <a
                            v-if="hasAnyPermission('asset.read')"
                            :href="route('assets.show', item.asset.id)"
                            class="truncate underline"
                            @click.prevent.stop="showAssetDetails(item.asset.id)"
                        >
                            Curie ID: {{ item.asset.curieId }}
                        </a>
                        <span v-else class="truncate">Curie ID: {{ item.asset.curieId }}</span>
                    </div>
                </div>
            </div>
        </div>
    </a>
</template>
<script setup>
import { computed } from 'vue';
import { router, usePage } from '@inertiajs/vue3';
import { trans } from '@/mixins/i18n';
import TaskResolutionIcon from './TaskResolutionIcon.vue';
import Spinner from '@/Components/Spinner.vue';
import SkeletonLoader from '@/Components/SkeletonLoader.vue';
import TaskGroupBadge from '@/Components/TaskGroupBadge.vue';
import { determineTaskResolution, getDate, getTaskGroupColor, getTaskResolutionColor, getTaskStatusUpdatedAt, getTimeZoneInfo } from './utils';
import { isMobile } from '@/Utils/UseMedia.js';
import Check from '../../../icons/Check.vue';
import dateTimeMedium from '@/filters/dateTimeMedium.js';
import { FileText } from '@lucide/vue';
import { visitModal } from '@/Utils/visitModal';
import { hasAnyPermission } from '@/mixins/hasAnyPermission.js';

const page = usePage();
const currentLocale = computed(() => page.props.locale);

const props = defineProps({
    item: Object,
    showSkeleton: {
        type: Boolean,
        default: false,
    },
    loading: {
        type: Boolean,
        default: false,
    },
    selectable: {
        type: Boolean,
        default: true,
    },
    selected: {
        type: Boolean,
        default: false,
    },
    showWorkOrderColumn: {
        type: Boolean,
        default: false,
    },
});

const emits = defineEmits(['update-selected']);

const locationHierarchy = computed(() => (props.item.asset.location?.hierarchy || []).sort((a, b) => a.level - b.level));
const airport = computed(() => locationHierarchy.value[0]?.iataCode);
const specificLocation = computed(() => locationHierarchy.value[1]?.name);
const location = computed(() => {
    return locationHierarchy.value[3]?.name || locationHierarchy.value[2]?.name || '';
});

const groupColor = computed(() => getTaskGroupColor(props.item?.group));
const taskResolvedOrTimedOut = computed(() => props.item?.resolved_at || props.item?.is_overdue);

const getApprovalTooltip = (item) => {
    if (!item) return;

    if (!item.approved_by) {
        return trans('quality_assurance.configuration.modal.requires_approval.label');
    }

    const approvedAt = dateTimeMedium(item.approved_at, currentLocale.value);
    return `${trans('general.label.approved_by')} ${item?.approved_by.lastName} <br/>${approvedAt}`;
};

const showAssetDetails = (assetId) => {
    router.visit(route('assets.show', assetId));
};

const openWorkOrder = (url) => {
    if (!url) {
        return;
    }
    visitModal(url, { preserveScroll: true });
};
</script>
